import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDataProvider, useNotify } from 'react-admin';

const TAB_NAMES = ['snapshot', 'licences', 'education', 'employment', 'certifications', 'accountability'];

interface UseDriverProfileTabsReturn {
  tabValue: number;
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  licenseTypes: any[];
  certificationTypes: any[];
  educationSchools: any[];
}

/**
 * Custom hook for managing driver profile tabs and reference data
 * Handles tab navigation with URL parameters and loads reference data
 */
export const useDriverProfileTabs = (): UseDriverProfileTabsReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [tabValue, setTabValue] = useState(() => {
    const tabParam = searchParams.get('tab') || 'snapshot';
    return TAB_NAMES.indexOf(tabParam) >= 0 ? TAB_NAMES.indexOf(tabParam) : 0;
  });

  const [licenseTypes, setLicenseTypes] = useState<any[]>([]);
  const [certificationTypes, setCertificationTypes] = useState<any[]>([]);
  const [educationSchools, setEducationSchools] = useState<any[]>([]);

  // Handle tab changes and update URL
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchParams({ tab: TAB_NAMES[newValue] || 'snapshot' });
  };

  // Load reference data
  useEffect(() => {
    Promise.all([
      dataProvider.getList('code-tables/license-types', { pagination: { page: 1, perPage: 1000 } }),
      dataProvider.getList('code-tables/certification-types', { pagination: { page: 1, perPage: 1000 } }),
      dataProvider.getList('code-tables/education-schools', { pagination: { page: 1, perPage: 1000 } }),
    ])
      .then(([licTypes, certTypes, schools]) => {
        setLicenseTypes(licTypes.data || []);
        setCertificationTypes(certTypes.data || []);
        setEducationSchools(schools.data || []);
      })
      .catch(error => console.error('Error loading reference data:', error));
  }, [dataProvider]);

  return {
    tabValue,
    handleTabChange,
    licenseTypes,
    certificationTypes,
    educationSchools,
  };
};
